import React, {useEffect, useMemo, useState} from 'react';
import { connect } from 'react-redux';
import {Space} from 'antd';
import {useNavigate} from 'react-router-dom';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import SpinSmall from '../../../SpinSmall';
import InvoiceDetailsCard from '../InvoiceDetailsCard';
import InvoiceNavigationButtons from '../InvoiceNavigationButtons';
import TransactionMatchEditor from '../TransactionMatchEditor';
import AuthenticationWindow from '../../CardsPage/AuthenticationWindow';
import {
  invoicesHelpers,
  objectHelpers,
  scaHelpers,
  systemHelpers,
  transactionsHelpers
} from '../../../../utils/helpers';
import { firebaseEvents } from '../../../../snippets/firebase';
import routes from '../../../../routes/routes.json';
import {
  expenseTypesConstants,
  invoicePaymentStatusConstants,
  invoiceStatusConstants,
  SCAActionsConstants
} from '../../../../constants';
import { invoicesActions } from '../../../../state/actions/invoice.actions';
import { expenseActions } from '../../../../state/actions/expense.actions';
import { transactionActions } from '../../../../state/actions/transaction.actions';

const transactionAuthActionName = SCAActionsConstants.TRANSACTIONS_WITHIN_90_DAYS_LOADING_ACTION;

const transactionActionsNames = {
  LOAD_LIST: 'LOAD_LIST',
  LOAD_MATCHED: 'LOAD_MATCHED'
};

const panelTypes = {
  MATCHING: 'matching',
  PAYMENT: 'payment',
};

const { MATCHING, PAYMENT } = panelTypes;
const { LOAD_LIST, LOAD_MATCHED } = transactionActionsNames;
const defaultSelectedTransaction = { id: undefined, external: false };

const InvoiceOverview = ({
  invoice,
  onEdit,
  onUpdate,

  dispatch,
  getInvoiceNeighbours,
  getInvoiceRelatedExpenses,
  getTransactions,
  invoicesListQuery,
  linkExpense,
  payInvoiceOutside,
  unlinkExpense,
  updateExpense,

  ...rest
}) => {
  const [activeAction, setActiveAction] = useState(false);
  const [activePanelType, setActivePanelType] = useState(MATCHING);
  const [authWindowProps, setAuthWindowProps] = useState({ open: false });
  const [isAvailableSuggested, setIsAvailableSuggested] = useState(true);
  const [isDefinedTransaction, setIsDefinedTransaction] = useState(false);
  const [loading, setLoading] = useState(false);
  const [neighborIds, setNeighborIds] = useState({ prev: null, next: null });
  const [transactionsQuery, setTransactionsQuery] = useState({});
  const [transactions, setTransactions] = useState({ searched: [], suggested: [] });
  const [matchedTransaction, setMatchedTransaction] = useState(undefined);
  const [selectedTransaction, setSelectedTransaction] = useState(defaultSelectedTransaction);

  const [t] = useTranslation('main');
  const navigate = useNavigate();

  const isReadyToMatch = useMemo(
    () => selectedTransaction?.id || selectedTransaction.external,
    [selectedTransaction]
  );

  const isLatestRequestForInvoiceExpenses = useMemo(
    () => transactionsQuery.hasOwnProperty('transaction_id_in'),
    [transactionsQuery]
  );

  // Reset selected transaction when invoice changes
  useEffect(() => {
    if (!objectHelpers.arraysIsEqual(selectedTransaction, defaultSelectedTransaction)) {
      setSelectedTransaction(defaultSelectedTransaction);
    }
  }, [invoice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load neighbor invoice ids
  useEffect(() => {
    if (!invoice) return;
    getInvoiceNeighbours({
      id: invoice.id,
      query: invoicesListQuery,
      successCallback: (data) => {
        setNeighborIds({
          prev: data?.previous_invoice_id,
          next: data?.next_invoice_id
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.id]);

  const payButtonProps = useMemo(
    () => ({ enabled: activePanelType === MATCHING && !isDefinedTransaction && !selectedTransaction.external }),
    [activePanelType, isDefinedTransaction, selectedTransaction?.external]
  );

  const previousButtonProps = useMemo(
    () => ({ disabled: !Boolean(neighborIds.prev) }),
    [neighborIds]
  );

  const nextButtonProps = useMemo(
    () => ({
      disabled: isReadyToMatch ? false : !Boolean(neighborIds.next),
      type: isReadyToMatch ? 'primary' : 'default'
    }),
    [isReadyToMatch, neighborIds]
  );

  // Load invoice expenses and set transaction state
  useEffect(() => {
    if (!invoice) return;
    const { id } = invoice;
    const isMatched = invoice?.status === invoiceStatusConstants.MATCHED;
    if (isMatched !== isDefinedTransaction) setIsDefinedTransaction(isMatched);
    loadInvoiceExpenses({ id, isMatched, initial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  const startLoading = () => setLoading(true);

  const finishLoading = () => setLoading(false);

  const finishTransactionsLoading = (data = [], type = 'searched') => {
    setTransactions({...transactions, [type]: data});
    finishLoading();
  }

  const finishMatchedTransactionsLoading = (transactions = []) => {
    setMatchedTransaction(transactions?.[0]);
    finishLoading();
  }

  const loadTransactions = ({isMatched = false, type = 'searched', query} = {}) => {
    if (!loading) startLoading();

    const actionName = isMatched ? LOAD_MATCHED : LOAD_LIST;

    if (activeAction !== actionName) setActiveAction(actionName);

    query = {
      amount_gte: 0.01,
      ...query,
      is_incoming: false,
      payment_status_not_in: transactionsHelpers.getDeclinedTransactionStatuses(),
      sort_order: 'desc'
    }

    getTransactions({
      headers: scaHelpers.getAuthHeaders(transactionAuthActionName),
      query,
      successCallback: (data) => isMatched ? finishMatchedTransactionsLoading(data) : finishTransactionsLoading(data, type),
      errorCallback: (response) => {
        scaHelpers.SCAResponseCallback({
          response,
          scaCallback: (scaProps) => setAuthWindowProps({...authWindowProps, ...scaProps}),
          errorCallback: () => isMatched ? finishMatchedTransactionsLoading() : finishTransactionsLoading([], type)
        });
      }
    });
  }

  const loadInvoiceExpenses = ({id, initial = false, isMatched = false}) => {
    if (!isMatched) {
      startLoading();

      getInvoiceRelatedExpenses({
        id,
        successCallback: (data) => {
          const transactionIds = data.map(e => e.transaction_id).filter(Boolean);

          if (!data.length || !transactionIds.length) {
            if (initial) {
              setIsAvailableSuggested(false);
              setLoading(false);
            } else {
              finishLoading();
              if (transactions.suggested.length) setTransactions({...transactions, suggested: []});
            }
            return;
          }

          const query = {transaction_id_in: transactionIds};
          setTransactionsQuery(query);
          loadTransactions({type: 'suggested', query});
        },
        errorCallback: () => finishTransactionsLoading([], 'suggested')
      });
    } else {
      const transactionId = invoice.expenses?.[0]?.transaction_id;
      if (!transactionId) return;

      const query = {transaction_id_in: [transactionId]};
      setTransactionsQuery(query);
      loadTransactions({isMatched: true, query});
    }
  }

  const handlePayNowClick = () => setActivePanelType(PAYMENT);

  const handleBackClick = () => setActivePanelType(MATCHING);

  const goToInvoice = (invoiceId) => {
    if (!invoiceId) return;

    navigate(`${routes.invoicesList}/${invoiceId}`);
  }

  const handlePreviousClick = () => {
    if (previousButtonProps.disabled) return;

    goToInvoice(neighborIds.prev);
  }

  const handleMoveNext = () => {
    goToInvoice(neighborIds.next);
  }

  const handleLinkAndUpdateExpense = (invoice, expenseId) => {
    const id = invoice?.id;
    if (!id) return;

    linkExpense({
      id,
      data: {expense_id: expenseId},
      successCallback: (invoice) => {
        updateExpense({
          id: expenseId,
          data: {expense_type: expenseTypesConstants[invoice.type]},
          propertyName: 'expense_type',
          successCallback: () => handleMoveNext(invoice),
          errorCallback: finishLoading
        });
      },
      errorCallback: finishLoading
    });
  };

  const handlePaidOutside = ({paid = true, successCallback} = {}) => {
    if (!invoice) return;

    payInvoiceOutside({
      id: invoice.id,
      paid,
      successCallback,
      errorCallback: finishLoading
    });
  }

  const handleMatchTransaction = () => {
    if (!invoice) return;

    if (!selectedTransaction.id && !selectedTransaction.external) return;

    setLoading(true);

    if (selectedTransaction.external) {
      handlePaidOutside({ successCallback: handleMoveNext });
    } else {
      const transactionsList = [...transactions.suggested, ...transactions.searched];
      const expenseId = transactionsList.find(transaction => transaction.id === selectedTransaction.id)?.expense_id;
      if (!expenseId) {
        setLoading(false);
        return;
      }

      const isCurrentlyPaidOutside = invoice.payment_status === invoicePaymentStatusConstants.PAID_OUTSIDE;

      if (isCurrentlyPaidOutside) {
        handlePaidOutside({
          paid: false,
          successCallback: () => handleLinkAndUpdateExpense(invoice, expenseId)
        });
      } else {
        handleLinkAndUpdateExpense(invoice, expenseId);
      }
    }
  }

  const handleRemoveMatching = () => {
    const expense = invoice?.expenses?.[0];
    const expenseId = expense?.expense_id;
    if (expenseId) {
      unlinkExpense({ data: {expense_id: expenseId}, successCallback: onUpdate });
    } else if (invoice.payment_status === invoicePaymentStatusConstants.PAID_OUTSIDE) {
      handlePaidOutside({ paid: false, successCallback: onUpdate });
    }
  }

  const handleNextClick = () => {
    const nextPage = neighborIds.next;
    if (isReadyToMatch) {
      handleMatchTransaction();
    } else if (Boolean(nextPage)) {
      goToInvoice(nextPage);
    }
  }

  const handleOkEditModal = () => {
    handleRemoveMatching();
    loadInvoiceExpenses({id: invoice?.id});
    setIsDefinedTransaction(false);
    setActivePanelType(MATCHING);
  }

  const handleCloseAuthModal = () => {
    finishLoading();
    setAuthWindowProps({...authWindowProps, open: false});
  }

  const handleOnSuccessAuth = () => {
    const actions = {
      [LOAD_LIST]: () => loadTransactions({query: transactionsQuery}),
      [LOAD_MATCHED]: () => loadTransactions({isMatched: true, query: transactionsQuery}),
    };

    setAuthWindowProps({...authWindowProps, open: false});
    actions[activeAction]?.();
  }

  const handleLoadInvoiceExpenses = () => {
    if (!invoice || !invoice.id) return;

    const isMatched = invoice.status === invoiceStatusConstants.MATCHED;
    loadInvoiceExpenses({id: invoice.id, isMatched});
  }

  const handleFilterTransactions = (params) => {
    const baseQuery = invoicesHelpers.convertMatchFilterPropsToApi(params);
    const hasNoFilters = objectHelpers.allValuesEmpty(baseQuery);

    const query = {
      ...baseQuery,
      has_invoice: false
    };

    setTransactionsQuery(query);

    if (hasNoFilters) {
      if (!isLatestRequestForInvoiceExpenses) {
        handleLoadInvoiceExpenses();
      }
      return;
    }

    loadTransactions({query});
  };

  const handleCloseFilter = (closed = false) => {
    if (closed || isLatestRequestForInvoiceExpenses || isReadyToMatch) return;

    handleLoadInvoiceExpenses();
  };

  const handleSelectTransaction = ({type, value} = {}) => {
    const isExternal = type === 'external';
    setSelectedTransaction({
      id: isExternal ? undefined : value,
      external: isExternal ? value : false
    });
  }

  const handlePaymentSuccess = () => {
    onUpdate?.();
    finishLoading();
    systemHelpers.logEvent(firebaseEvents.INVOICES_EDIT_INVOICE_TRANSACTION);
  }

  return (
    <Space direction='vertical' size='middle' {...rest}>
      <InvoiceNavigationButtons
        backButtonProps={{
          enabled: activePanelType === PAYMENT,
          onClick: handleBackClick
        }}
        invoice={invoice}
        neighborIds={neighborIds}
        previousButtonProps={{
          ...previousButtonProps,
          enabled: true,
          onClick: handlePreviousClick
        }}
        nextButtonProps={{
          ...nextButtonProps,
          enabled: true,
          onClick: handleNextClick
        }}
        payButtonProps={{
          ...payButtonProps,
          onClick: handlePayNowClick
        }}
      />

      <InvoiceDetailsCard
        invoice={invoice}
        isVendorEditDisabled={isDefinedTransaction}
        onEdit={onEdit}
      />

      <SpinSmall spinning={loading}>
        <TransactionMatchEditor
          activePanelType={activePanelType}
          isAvailableSuggested={isAvailableSuggested}
          isDefinedTransaction={isDefinedTransaction}
          invoice={invoice}
          loading={loading}
          matchedTransaction={matchedTransaction}
          onCloseFilter={handleCloseFilter}
          onEditModalOk={handleOkEditModal}
          onFilter={handleFilterTransactions}
          onNext={handleMoveNext}
          onPaymentSuccess={handlePaymentSuccess}
          onSelectTransaction={handleSelectTransaction}
          transactions={transactions}
        />
      </SpinSmall>

      <AuthenticationWindow
        {...authWindowProps}
        authModalProps={{ title: `${t('transactions')} ${t('loading')}` }}
        handleCancel={handleCloseAuthModal}
        onSuccess={handleOnSuccessAuth}
        operationName={transactionAuthActionName}
      />
    </Space>
  );
};

InvoiceOverview.propTypes = {
  invoice: PropTypes.object,
  onEdit: PropTypes.func,
  onUpdate: PropTypes.func
};

const mapStateToProps = ({ invoice: { invoicesListQuery } }) => ({
  invoicesListQuery
});

const mapDispatchToProps = {
  getInvoiceNeighbours: invoicesActions.getInvoiceNeighbours,
  getInvoiceRelatedExpenses: invoicesActions.getInvoiceRelatedExpenses,
  getTransactions: transactionActions.getTransactionsList,
  linkExpense: invoicesActions.linkExpense,
  payInvoiceOutside: invoicesActions.payInvoiceOutside,
  unlinkExpense: invoicesActions.unlinkExpense,
  updateExpense: expenseActions.updateExpense
};

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceOverview);
